import { Fragment, useState, useContext, useEffect, useCallback } from 'react';
import { Button, Table, FormControl, Row, Col, Form, ButtonGroup } from 'react-bootstrap'

// For Login related items
import { AuthContext } from "./Login/AuthContext"
import { DatabaseContext } from './DatabaseContext';

// Common interface items
import Icons from './common/Icons';
import ProtectedComponent from './common/ProtectedComponent';
import { dateYYYYMMMDD, validatePositiveInt, validateAlphanumeric } from '../services/commonParse';

// Interaction with database
import { updateBPBatch, createCodeList, createXMLnewparts, createXMLqcgrade } from '../services/baseplateService';
import { addShipment, updateShipment, deleteShipment, destinationOptions } from '../services/shipmentService';
import { HGCFormRowText, HGCFormRowDate, HGCFormRowSelect } from './common/SimpleForm';
import { SimpleFold } from './common/misc';
// Additional interface items
import TableViewAdd, { defaultAdd, defaultResetAdd, defaultValidateAdd } from './common/TableViewAdd';
import TableViewEdit, { defaultEdit, defaultReset, defaultValidateEdit } from './common/TableViewEdit';
import PaginateTable, { CategoryFilter, FuzzyTextFilter, debounce } from './common/TableDisplay';
import { createRowObj, filterTable, findTableRow, tabField } from '../services/tableUtils';


const ShipmentColumns = () => [
  { Header: 'Shipment', Cell: ShipmentCellID, Filter: FuzzyTextFilter((ship) => [ship.box_desc]) },
  { Header: 'Destination', Cell: (ship) => ship.destination, Filter: CategoryFilter((ship) => ship.destination) },
  { Header: 'Date', Cell: (ship) => dateYYYYMMMDD(ship.ship_date) },
  { Header: 'Tracking', accessor: 'stt_idx', Cell: ShipmentCellTracking },
  { Header: 'Contents', accessor: 'id', Cell: ShipmentCellContents, minWidth: "60%" },
]

const ShipmentCellID = (shipment) => {
  const id = shipment.id;
  const { user } = useContext(AuthContext);
  const { baseplateList, updateBaseplateList, updateShipmentList } = useContext(DatabaseContext);

  // For showing single items
  const [showEdit, setShowEdit] = useState(false);
  const removeAction = async () => {
    const removeList = tabField(filterTable(baseplateList, (bp) => bp.shipment_id === id), 'id')
    await updateBPBatch({ idList: removeList, shipment_id: null }).then(
      await deleteShipment(id).then(
        async () => {
          updateShipmentList();
          updateBaseplateList();
        }
      )
    )
  }

  return <Fragment>
    {shipment.box_desc}<br />
    <Button variant='light' size='sm' onClick={() => { setShowEdit(true) }}>{Icons.edit}</Button>
    {user.role === "admin" && <Button variant='light' size='sm' onClick={removeAction}>{Icons.remove}</Button>}
    {showEdit && <ShipmentEdit id={id} showState={[showEdit, setShowEdit]} />}
  </Fragment>
}

const ShipmentCellTracking = (shipment) => {
  const makeLink = (stt_idx) => <a href={`https://cmsr-shipment.web.cern.ch/tracking_view/${stt_idx}`}> STT {stt_idx} 🔗</a >
  return <Fragment>{shipment.stt_idx === null ? "--" : makeLink(shipment.stt_idx)}</Fragment>
}

const ShipmentCellContents = (shipment) => {
  const { baseplateList } = useContext(DatabaseContext);

  // Getting the list of baseplates associated with the shipment of interest
  const qualityCache = createBPQualityCache(filterTable(baseplateList, (bp) => bp.shipment_id === shipment.id));
  const barcode_list = shipment.bar_codelist.filter((x) => x !== null);
  const fullIDList = shipment.idlist.filter((x) => x !== null);
  const idNoCentral = shipment.idlist.filter((v, i) => !shipment.is_db_central_list[i] && v !== null);
  const idNoCentralQC = shipment.idlist.filter((v, i) => !shipment.is_db_central_qc_list[i] && v !== null);
  const estMass = (shipment.weight_sum + 2250 + 6 * shipment.idlist.length) / 1000; //Adding mass of standard shipment box and 6 grams to each item, cast to kg

  // Display for warning messages
  const [warning,] = _validateShipmentList(barcode_list, qualityCache, shipment.id, shipment.destination);

  return <Fragment>
    <ShipmentCellContentsSummary barCodeList={barcode_list} estMass={estMass} />
    <SimpleFold header="Full List">
      <ShipmentCellContentsDetail barCodeList={barcode_list} warning={warning} />
    </SimpleFold>
    <ShipmentCellContentsExport fullList={barcode_list} noEntryList={idNoCentral} noQCList={idNoCentralQC} fullIDList={fullIDList} shipment={shipment} />
  </Fragment >
}

const splitBarCodeType = (barcode_list) => {
  return [
    barcode_list.filter((code) => code[5] === "F" && code[7] === "W").sort(),
    barcode_list.filter((code) => code[5] !== "F" && code[7] === "W").sort(),
    barcode_list.filter((code) => code[5] === "F" && code[7] === "T").sort(),
    barcode_list.filter((code) => code[5] !== "F" && code[7] === "T").sort(),
  ]
}

const ShipmentCellContentsSummary = ({ barCodeList, estMass }) => {
  const [CuWFull, CuWPartial, TiFull, TiPartial] = splitBarCodeType(barCodeList);
  return <> Total ({barCodeList.length}, {estMass.toFixed(1)} kg.): {[
    "CuW Fulls (" + CuWFull.length + ")",
    "CuW Partials (" + CuWPartial.length + ")",
    "Ti Fulls (" + TiFull.length + ")",
    "Ti Partials (" + TiPartial.length + ")"
  ].join(", ")} </>
}

const ShipmentCellContentsDetail = ({ barCodeList, warning }) => {
  const [CuWFull, CuWPartial, TiFull, TiPartial] = splitBarCodeType(barCodeList);
  return <>
    <Table bordered striped>
      <tbody>
        {CuWFull.length > 0 && <tr><td style={{ minWidth: "6em" }}>CuW Full</td><td>{CuWFull.join(", ")}</td></tr>}
        {CuWPartial.length > 0 && <tr><td style={{ minWidth: "6em" }}>CuW Partial</td><td>{CuWPartial.join(", ")}</td></tr>}
        {TiFull.length > 0 && <tr><td style={{ minWidth: "6em" }}>Ti Full</td><td>{TiFull.join(", ")}</td></tr>}
        {TiPartial.length > 0 && <tr><td style={{ minWidth: "6em" }}>Ti Partial</td><td>{TiPartial.join(", ")}</td></tr>}
        {
          Object.keys(warning).map((t, idx) => {
            return warning[t].length > 0 && <tr key={`warning-${idx}`}>
              <td style={{ minWidth: "20em", color: "red" }}>{t}</td>
              <td style={{ textAlign: "left" }}>{warning[t].join(", ")}</td>
            </tr>
          })
        }
      </tbody>
    </Table>
  </>
}

const ShipmentCellContentsExport = ({ fullList, noEntryList, noQCList, fullIDList, shipment }) => {
  const box = shipment.box_desc.includes(shipment.destination) ? shipment.box_desc : shipment.destination + shipment.box_desc;

  return <div style={{ display: "flex", justifyContent: "space-between", marginTop: "0.5em" }}>
    <ButtonGroup>
      <Button variant="primary" disabled={fullList.length === 0} onClick={() => createCodeList(fullList, box)}>
        Download barcode list
      </Button>
      <Button variant='secondary' disabled={noEntryList.length === 0} onClick={() => createXMLnewparts(noEntryList, box)}>
        {noEntryList.length ? `Export missing parts XML (${noEntryList.length} pieces)` : "All parts uploaded!"}
      </Button>
      <Button variant='success' disabled={noQCList.length === 0} onClick={() => createXMLqcgrade(noQCList, box)}>
        {noQCList.length ? `Export missing QC XML (${noQCList.length} pieces)` : "All QC uploaded!"}
      </Button>
    </ButtonGroup>

    <ButtonGroup>
      <Button variant='secondary' disabled={fullList.length === 0} onClick={() => createXMLnewparts(fullIDList, "Full-" + box)}>
        Export all new parts XML {Icons.notebook}
      </Button>
      <Button variant='success' disabled={fullList.length === 0} onClick={() => createXMLqcgrade(fullIDList, "Full-" + box)}>
        Export all QC grade XML {Icons.notebook}
      </Button>
    </ButtonGroup>
  </div>
}

const createBPQualityCache = (baseplateList) => {
  // We will create a cache results with the barcode as the lookup key whenever a draw element is called,
  // this reduces the requirement the expensive parse everytime
  const cache = {};
  if (!('id' in baseplateList)) return cache;

  const dest_compat = {
    'FLW': ["CMU", "IHEP", "TIFR", "TTU", "NTU", "UCSB"],
    "FHW": ["NTU"],
    "LLW": ["NTU"],
    "RLW": ["NTU"],
    "TLW": ["NTU"],
    "BLW": ["NTU"],
    "5LW": ["NTU"],
    "LHW": ["NTU"],
    "RHW": ["NTU"],
    "BHW": ["NTU"],
    "FLT": ["CMU", "TTU", "UCSB"],
    "FHT": ["UCSB"],
    "LLT": ["UCSB"],
    "RLT": ["UCSB"],
    "TLT": ["UCSB"],
    "BLT": ["UCSB"],
    "5LT": ["UCSB"],
    "THT": ["UCSB"]
  }

  const origin = (bp) => {
    if (bp.bare_barcode === null) { return "EU"; }
    if (bp.bare_barcode.substring(8, 10) === "II") { return "II"; }
    if (bp.bare_barcode.substring(8, 10) === "IH") { return "IH"; }
    return "EU";
  }

  for (let i = 0; i < baseplateList.id.length; ++i) {
    if (!baseplateList.bar_code[i]) continue;
    const bp = createRowObj(baseplateList, i);
    const warning = []
    if ((bp.weight_bare === null && bp.thickness === null) || bp.weight_lam === null) {
      warning.push("Missing manual measurements");
    }
    if (bp.radius0 === null) {
      warning.push("Missing 2D measurements");
    }
    if (bp.flatness_bare_l === null && bp.flatness_bare_r === null) {
      warning.push("Missing bare flatness");
    }
    if (bp.flatness_lam === null || bp.height_lam0 === null) {
      warning.push("Missing lam. flatness");
    }
    if (bp.flatness_grade === "red") {
      warning.push("Red-grade flatness");
    }
    if (bp.tolerance_grade === "red" || !bp.diameter_hole_passed || !bp.width_slot_passed) {
      warning.push("Red-grade tolerance");
    }
    if (bp.kapton_id === null) {
      warning.push("Missing Kapton");
    }
    if (bp.kapton_align_passed !== true || Number(bp.defect_lam_delam_pad) !== 0 || Number(bp.defect_lam_glue_hole) !== 0 || Number(bp.defect_lam_glue_pad) !== 0) {
      warning.push("Kapton Lamination defects");
    }
    if (bp.tape_id === null) {
      warning.push("Missing Tape");
    }
    if (!bp.is_db_central) {
      warning.push("Missing part in CMSR");
    }
    if (!bp.is_db_central_qc) {
      warning.push("Missing QC in CMSR");
    }
    // Returning the reduced item
    cache[bp.bar_code] = {
      'id': bp.id, // Required for downstream processing
      'shipment_id': bp.shipment_id, // Shipment is not known at the moment of caching
      'valid_location': dest_compat[bp.bar_code.substring(5, 8)],// destination is not known
      'warning': warning,
      'origin': origin(bp),
    }
  }

  return cache;
}



const _validateShipmentList = (barcode_list, qualityCache, shipment_id, destination) => {
  var idList = []; // List of all ID that we are looking for
  const warningMessage = {}; // List of all potential issues

  const appendWarning = (message, barcode) => {
    if (!(message in warningMessage)) {
      warningMessage[message] = [];
    }
    warningMessage[message].push(barcode);
  }

  const originCount = { "II": 0, "IH": 0, "EU": 0 };

  for (const barcode of barcode_list) {
    if (barcode === "" || barcode === null) continue;
    if (barcode.length !== 15 || !barcode.startsWith('320BA')) {
      appendWarning("Unknown barcode", barcode);
      continue;
    }
    if (!(barcode in qualityCache)) {
      appendWarning("Unknown barcode", barcode);
      continue;
    }
    idList.push(qualityCache[barcode].id);
    for (const warn of qualityCache[barcode].warning) {
      appendWarning(warn, barcode)
    }
    if (qualityCache[barcode].shipment_id !== null && qualityCache[barcode].shipment_id !== shipment_id) {
      appendWarning("Already assigned to other shipment", barcode);
    }
    if (qualityCache[barcode].valid_location.indexOf(destination) === -1) {
      appendWarning("Incompatible destination", barcode);
    }
    // Increment the number of origin
    originCount[qualityCache[barcode].origin]++;
  }
  // Reloop
  const maxOrigin = Object.keys(originCount).reduce(function(a, b) { return originCount[a] > originCount[b] ? a : b });
  for (const barcode of barcode_list) {
    if (barcode === "" || barcode === null) continue;
    if (barcode.length !== 15 || !barcode.startsWith('320BA')) continue;
    if (!(barcode in qualityCache)) continue;
    if (qualityCache[barcode].origin !== maxOrigin) {
      appendWarning("Multiple origin detected", barcode)
    }
  }
  return [warningMessage, idList];
}

const validateShipmentList = (input, setBaseplateIDList, setWarningMessage, qualityCache, shipment_id, destination) => {
  const [newWarn, newIdList] = _validateShipmentList(
    input.split(/[\s,]+/)
      .filter((t) => t !== "") // Remove blanks
      .filter((v, i, arr) => arr.indexOf(v) === i), // Striping to unique
    qualityCache, shipment_id, destination
  );
  setWarningMessage(newWarn);
  setBaseplateIDList(newIdList);
}

const DisplayInputWarning = ({ found, warnings }) => {
  return <div>
    <Row>
      <Col>Found baseplates</Col>
      <Col>{found.length}</Col>
    </Row>
    {Object.keys(warnings).filter((key) => ((warnings[key].length > 0) && !key.endsWith("CMSR"))).map((key) =>
      <Fragment key={`fragment-${key}`}>
        <hr />
        <Row>
          {key}</Row>
        <Row>
          {warnings[key].map((bp) => `${bp}`).join(", ")}
        </Row>
      </Fragment>
    )}
  </div>
}

const ShipmentAdd = ({ showState }) => {
  const { baseplateList, updateBaseplateList, updateShipmentList } = useContext(DatabaseContext);
  const [, setShow] = showState;
  const [invalid, setInvalid] = useState("");
  const form = {
    "box_desc": useState(null),
    "ship_date": useState(new Date()),
    "destination": useState(null),
    "stt_idx": useState(null),
  }
  const [idList, setIDList] = useState([]) // For to be passed to the database API
  const [baseplateInput, setBaseplateInput] = useState("") // to be used for element displays
  // eslint-disable-next-line
  const errorForm = {
    "box_desc": useState(""),
    "destination": useState(""),
    "stt_idx": useState(""),
  };
  const [baseplateInputWarning, setBaseplateInputWarning] = useState({});

  const resetAction = () => {
    defaultResetAdd(form, { 'ship_date': () => new Date() })();
    setIDList([]);
    setBaseplateInput("");
  }
  const addActionStep1 = defaultAdd(form, addShipment, updateShipmentList, resetAction, setShow);
  const addAction = async () => {
    const newShip = (await addActionStep1());
    await updateBPBatch({ 'idList': idList, 'shipment_id': newShip.id })
    await updateBaseplateList();
    await updateShipmentList();
    window.location.reload(false);
  }

  useEffect(() => { setInvalid(defaultValidateAdd(errorForm)) }, [errorForm])
  const qualityCache = createBPQualityCache(baseplateList);

  //eslint-disable-next-line
  const handleInputField = useCallback(
    debounce((input) => { validateShipmentList(input, setIDList, setBaseplateInputWarning, qualityCache, null, form["destination"][0]) }, 200)
    , [baseplateInput, form["destination"][0]])

  return <TableViewAdd title="Add new shipment" showState={showState} addAction={addAction} invalid={invalid}>
    <HGCFormRowText label="Box-ID" dataState={form["box_desc"]} errorState={errorForm["box_desc"]} validate={validateAlphanumeric(true)} autoFocus={true} />
    <HGCFormRowDate label="Shipment date" dataState={form["ship_date"]} />
    <HGCFormRowSelect label="Destination" dataState={form["destination"]} errorState={errorForm["destination"]} options={destinationOptions} allowNull={false} />
    <HGCFormRowText label="CMSR STT index" dataState={form["stt_idx"]} errorState={errorForm["stt_idx"]} validate={validatePositiveInt(false)} />
    <Row style={{ marginBottom: "1.5em" }}>
      <Form.Label column sm={3}>CMS bar codes</Form.Label>
      <Col sm={3}>
        <FormControl
          text="text"
          as="textarea"
          value={baseplateInput}
          rows={30}
          style={{ textSizeAdjust: "50%" }}
          onChange={(e) => {
            setBaseplateInput(e.target.value);
            handleInputField(e.target.value);
          }}
        />
      </Col>
      <Col sm={4}>
        <DisplayInputWarning found={idList} warnings={baseplateInputWarning} />
      </Col>
    </Row>
  </TableViewAdd >
}

const ShipmentEdit = ({ id, showState }) => {
  const { baseplateList, shipmentList, updateBaseplateList, updateShipmentList } = useContext(DatabaseContext);
  const [, setShow] = showState;
  const shipment = findTableRow(shipmentList, (shipment) => shipment.id === id);
  const origIDList = shipment.idlist.filter((v) => v !== null);
  const origBarcodeList = shipment.bar_codelist.filter((v) => v !== null).sort();
  const [invalid, setInvalid] = useState("");
  const form = {
    "box_desc": useState(shipment.box_desc),
    "ship_date": useState(new Date(shipment.ship_date)),
    "destination": useState(shipment.destination),
    "stt_idx": useState(shipment.stt_idx),
  }
  const [idList, setIDList] = useState(origIDList);
  const [baseplateInput, setBaseplateInput] = useState(origBarcodeList.join("\n")) // to be used for element displays
  // eslint-disable-next-line
  const errorForm = {
    "box_desc": useState(""),
    "destination": useState(""),
    "stt_idx": useState(""),
    "baseplateInput": useState(""),
  };
  const [baseplateInputWarning, setBaseplateInputWarning] = useState({});

  const resetActionStep1 = defaultReset(form, shipment)
  const resetAction = () => {
    resetActionStep1();
    const origIDList = shipment.idlist;
    const origBarcodeList = shipment.bar_codelist.filter((v) => v !== null).sort();
    setIDList(origIDList);
    setBaseplateInput(origBarcodeList.join("\n"))
    validateShipmentList(origBarcodeList.join("\n"), setIDList, setBaseplateInputWarning, qualityCache, id, form["destination"][0]); // Run once each time th reset is defined
  }
  const editActionStep1 = defaultEdit(id, form, updateShipment, updateShipmentList, resetAction, setShow);
  const editAction = async () => {
    editActionStep1();
    await updateBPBatch({ 'idList': origIDList, 'shipment_id': null }); // Must be in this order
    await updateBPBatch({ 'idList': idList, 'shipment_id': id });
    updateBaseplateList();
    updateShipmentList();
    setShow(false);
    window.location.reload(false);
  }
  useEffect(() => { setInvalid(defaultValidateEdit(errorForm)) }, [errorForm])

  const qualityCache = createBPQualityCache(baseplateList);
  //eslint-disable-next-line
  const handleInputField = useCallback(
    debounce((input) => { validateShipmentList(input, setIDList, setBaseplateInputWarning, qualityCache, id, form["destination"][0]) }, 200)
    , [baseplateList])
  //eslint-disable-next-line
  useEffect(() => validateShipmentList(baseplateInput, setIDList, setBaseplateInputWarning, qualityCache, id, form["destination"][0]), [form["destination"][0]])

  return <TableViewEdit title="Edit shipment details" showState={showState} editAction={editAction} resetAction={resetAction} invalid={invalid}>
    <HGCFormRowText label="Box-ID" dataState={form["box_desc"]} errorState={errorForm["box_desc"]} validate={validateAlphanumeric(true)} />
    <HGCFormRowDate label="Shipment date" dataState={form["ship_date"]} />
    <HGCFormRowSelect label="Destination" dataState={form["destination"]} errorState={errorForm["destination"]} options={destinationOptions} allowNull={false} />
    <HGCFormRowText label="CMSR STT index" dataState={form["stt_idx"]} errorState={errorForm["stt_idx"]} validate={validatePositiveInt(false)} />
    <Row style={{ marginBottom: "1.5em" }}>
      <Form.Label column sm={3}>CMS bar codes</Form.Label>
      <Col sm={3}>
        <FormControl
          text="text"
          as="textarea"
          value={baseplateInput}
          rows={30}
          style={{ textSizeAdjust: "50%" }}
          onChange={(e) => {
            setBaseplateInput(e.target.value);
            handleInputField(e.target.value);
          }}
        />
      </Col>
      <Col sm={4}>
        <DisplayInputWarning found={idList} warnings={baseplateInputWarning} />
      </Col>
    </Row>
  </TableViewEdit>
}

export const ShipmentList = () => {
  const { shipmentList } = useContext(DatabaseContext);
  const [showAdd, setShowAdd] = useState(false);

  return <ProtectedComponent>
    <Button onClick={() => { setShowAdd(true) }}>Create new shipment</Button>
    <ShipmentAdd showState={[showAdd, setShowAdd]} />
    <hr />
    <PaginateTable tableData={shipmentList} columnLayout={ShipmentColumns()} />
  </ProtectedComponent>
}

export default ShipmentList;
